import { useState, forwardRef, useEffect } from 'react';
import useForkRef from '@mui/utils/useForkRef';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { unstable_useDateField as useDateField } from '@mui/x-date-pickers/DateField';
import { useClearableField } from '@mui/x-date-pickers/hooks';
import { Unstable_PickersSectionList as PickersSectionList } from '@mui/x-date-pickers/PickersSectionList';
import dayjs from 'dayjs';

const BrowserFieldRoot = styled('div', { name: 'BrowserField', slot: 'Root' })({
  display: 'flex',
  alignItems: 'center',
  border: '0px solid #F1F1F1',
  justifyContent: 'space-between',
  borderRadius: '8px',
});

const BrowserFieldContent = styled('div', { name: 'BrowserField', slot: 'Content' })({});

const BrowserTextField = forwardRef((props, ref) => {
  const {
    // These props are for internal use by the picker
    enableAccessibleFieldDOMStructure,
    elements,
    sectionListRef,
    contentEditable,
    onFocus,
    onBlur,
    tabIndex,
    onInput,
    onPaste,
    onKeyDown,
    onChange,
    value,
    label,
    areAllSectionsEmpty,
    disabled,
    readOnly,
    focused,
    error,
    InputProps: { ref: InputPropsRef, startAdornment, endAdornment } = {},
    ...other
  } = props;

  const handleRef = useForkRef(InputPropsRef, ref);

  return (
    <BrowserFieldRoot ref={handleRef} {...other}>
      {startAdornment}
      <BrowserFieldContent>
        <PickersSectionList
          elements={elements}
          sectionListRef={sectionListRef}
          contentEditable={contentEditable}
          onFocus={onFocus}
          onBlur={onBlur}
          tabIndex={tabIndex}
          onInput={onInput}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
        />
      </BrowserFieldContent>
      {endAdornment}
    </BrowserFieldRoot>
  );
});

const BrowserDateField = forwardRef((props, ref) => {
  const { slots, slotProps, ...textFieldProps } = props;

  const fieldResponse = useDateField({
    ...textFieldProps,
    enableAccessibleFieldDOMStructure: true,
  });

  const processedFieldProps = useClearableField({
    ...fieldResponse,
    slots,
    slotProps,
  });

  return <BrowserTextField ref={ref} {...processedFieldProps} />;
});

const BrowserDatePicker = forwardRef((props, ref) => {
  return (
    <DatePicker
      ref={ref}
      {...props}
      slots={{ field: BrowserDateField, ...props.slots }}
      // Update the format here to display "Friday | January 24, 2025"
      format="ddd | MMMM D, YYYY"
      className="w-full"
    />
  );
});

export default function CalendarDatePicker({ setDataFromChild, startDate, dateInput, name }) {
  const [date, setDate] = useState(dateInput ? dateInput : dayjs());
  // Sync date state with dateInput prop whenever it changes
  useEffect(() => {
    if (dateInput) {
      setDate(dateInput);
    }
  }, [dateInput]);


  const handleDate = (value) => {
    setDate(value);
    // Using dayjs to format the date consistently
    setDataFromChild({
      name: name,
      date: dayjs(value).format("dddd | MMMM D, YYYY"),
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <BrowserDatePicker
        slotProps={{
          field: { clearable: false },
        }}
        name={name}
        value={dayjs(date)}
        onChange={(newValue) => handleDate(newValue)}
        minDate={dayjs()}
      />
    </LocalizationProvider>
  );
}
